import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"

const RATE_LIMIT_SECONDS = 30
const EXIT_ERROR_COOLDOWN_MS = 5000

export default class extends Controller {
  static targets = [
    "connectButton", "loadingIndicator", "successIndicator",
    "errorIndicator", "verificationBadge", "revenueAmount", "confidenceScore",
    "rateLimitMessage"
  ]

  static values = {
    applicationId: String,
    linkTokenUrl: String,
    exchangeTokenUrl: String,
    url: String,
    frame: String,
    connectionState: { type: String, default: "none" }
  }

  #isConnecting = false

  connect() {
    this.checkPlaidSDK()
  }

  checkPlaidSDK() {
    // Give SDK a moment to initialize
    setTimeout(() => {
      if (!window.Plaid) {
        console.warn("Plaid SDK not loaded. Bank verification will not be available.")
      }
    }, 1000)
  }
  
  async connectBank(event) {
    event.preventDefault()

    const response = await post(this.urlValue, { responseKind: "turbo-stream" })

    if (response.ok) {
      const html = await response.html
      if (html && html.includes('<turbo-stream')) {
        Turbo.renderStreamMessage(html)
      }
    }
  }
  
  // Plaid SDK is loaded from CDN - no mock needed
  
  async connectWithPlaid(event) {
    event.preventDefault()

    if (this.#isConnecting ||
        this.connectionStateValue === "pending" ||
        this.connectionStateValue === "verified") {
      return
    }

    if (this.#isRateLimited()) {
      this.#showRateLimitMessage()
      return
    }

    this.#isConnecting = true
    this.#recordAttempt()
    this.showLoading("Preparing bank connection...")

    try {
      const linkTokenResponse = await post(this.linkTokenUrlValue, {
        responseKind: "json"
      })
      
      if (linkTokenResponse.statusCode === 401) {
        const errorData = await linkTokenResponse.json
        if (errorData.requires_authentication) {
          this.#resetConnecting()
          window.location.href = errorData.conversion_url
          return
        }
      }

      if (!linkTokenResponse.ok) {
        throw new Error(`Failed to create bank connection: ${linkTokenResponse.statusCode}`)
      }

      const linkTokenData = await linkTokenResponse.json
      this.initializePlaidLink(linkTokenData.link_token)

    } catch (error) {
      this.#resetConnecting()
      this.showError("Unable to connect to bank. Please try manual entry.")
    }
  }
  
  initializePlaidLink(linkToken) {
    if (!window.Plaid) {
      this.#resetConnecting()
      this.showError("Bank connection service not available. Please reload the page and try again.")
      return
    }

    const handler = window.Plaid.create({
      token: linkToken,
      onSuccess: (public_token, metadata) => {
        this.handlePlaidSuccess(public_token, metadata)
      },
      onExit: (err, metadata) => {
        document.body.classList.remove('plaid-modal-open')

        if (err != null) {
          console.error("Plaid Link error:", err)
          this.#resetConnecting()
          this.showError("Bank connection was unsuccessful. You can upload your bank statements instead.")
        } else {
          // User closed without error — restore after cooldown
          setTimeout(() => {
            this.#resetConnecting()
            this.hideLoading()
          }, EXIT_ERROR_COOLDOWN_MS)
        }
      },
      onEvent: (eventName, metadata) => {
        // Event tracking for analytics if needed
        console.log("Plaid event:", eventName, metadata)
      }
    })

    this.hideLoading() // Hide the "preparing" message before showing the modal

    // Add class to body to prevent scrolling and ensure proper modal positioning on mobile
    document.body.classList.add('plaid-modal-open')

    handler.open()
  }
  
  async handlePlaidSuccess(publicToken, metadata) {
    document.body.classList.remove('plaid-modal-open')
    this.connectionStateValue = "pending"
    this.showLoading("Analyzing your revenue data...")

    try {
      const response = await post(this.exchangeTokenUrlValue, {
        body: JSON.stringify({ public_token: publicToken, metadata: metadata }),
        responseKind: "turbo-stream",
        headers: { "Content-Type": "application/json" }
      })

      if (response.ok) {
        const html = await response.html
        if (html && html.includes('<turbo-stream')) {
          Turbo.renderStreamMessage(html)
        }
        // Button intentionally left disabled — Turbo Stream will replace this section
        this.hideLoadingOnly()
      } else {
        sessionStorage.setItem('plaid_connection_failed', 'Bank verification failed. You can upload bank statements instead.')
        Turbo.visit(window.location.href, { action: "replace" })
      }

    } catch (error) {
      sessionStorage.setItem('plaid_connection_failed', 'Bank connection failed. You can upload bank statements instead.')
      Turbo.visit(window.location.href, { action: "replace" })
    }
  }
  
  // Manual revenue entry is now handled purely as form field updates
  // No separate submission needed - it's part of the main application form
  
  showLoading(message) {
    this.hideAllStates()
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.remove('hidden')
      this.loadingIndicatorTarget.querySelector('.loading-message').textContent = message
    }
    
    // Disable buttons during loading
    if (this.hasConnectButtonTarget) this.connectButtonTarget.disabled = true
  }
  
  showSuccess(result) {
    this.hideAllStates()
    
    // Replace the entire verification section with the verified state
    this.replaceWithVerifiedState(result)
    
    // Update the hidden form field for form submission
    const monthlyRevenueField = document.querySelector('input[name="application[monthly_revenue]"]')
    if (monthlyRevenueField) {
      monthlyRevenueField.value = result.verified_monthly_revenue
    }
    
    // Show success message
    this.showSuccessMessage(result.message)
    
    // Re-enable buttons
    this.enableButtons()
  }
  
  replaceWithVerifiedState(result) {
    // CRITICAL FIX: Instead of replacing innerHTML (which breaks form structure),
    // update specific elements to preserve the form integrity and Continue button functionality
    
    // Hide connection options without destroying the DOM structure
    const connectButton = this.element.querySelector('[data-action="click->plaid-link#connectWithPlaid"]')
    const manualButton = this.element.querySelector('[data-controller="toggle"]')
    const benefitsSection = this.element.querySelector('.bg-white.rounded-lg.border.border-blue-200')
    const manualForm = this.element.querySelector('#manual-revenue-form')
    
    if (connectButton) connectButton.style.display = 'none'
    if (manualButton) manualButton.style.display = 'none'
    if (benefitsSection) benefitsSection.style.display = 'none'
    if (manualForm) manualForm.style.display = 'none'
    
    // Create the verified state HTML as a new element (not innerHTML replacement)
    const verifiedDiv = document.createElement('div')
    verifiedDiv.className = 'verification-success-content'
    verifiedDiv.innerHTML = `
      <div class="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
        <div class="flex items-center justify-between">
          <div>
            <div class="flex items-center mb-2">
              <span data-plaid-link-target="verificationBadge">
                <div class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${result.verification_badge.color}-100 text-${result.verification_badge.color}-800">
                  <svg class="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  ${result.verification_badge.text}
                </div>
              </span>
            </div>
            <div class="text-lg font-semibold text-gray-900">
              Monthly Revenue: <span data-plaid-link-target="revenueAmount">${this.formatCurrency(result.verified_monthly_revenue)}</span>
            </div>
            <div class="text-sm text-gray-600" data-plaid-link-target="confidenceScore">
              ${Math.round(result.confidence_score * 100)}% confidence
            </div>
          </div>
          <button type="button" class="text-sm text-blue-600 hover:text-blue-800" onclick="location.reload()">
            Update
          </button>
        </div>
      </div>
      
      <div class="bg-white rounded-lg border border-green-200 p-4 mb-4">
        <h4 class="font-medium text-gray-900 mb-2 flex items-center">
          <svg class="w-4 h-4 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4" />
          </svg>
          Revenue Successfully Verified
        </h4>
        <div class="space-y-1 text-sm text-gray-600">
          <div class="flex items-center">
            <svg class="w-4 h-4 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
            ${result.verification_badge.description}
          </div>
          <div class="flex items-center">
            <svg class="w-4 h-4 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
            Better rates available for verified revenue
          </div>
          <div class="flex items-center">
            <svg class="w-4 h-4 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
            Ready to continue to loan configuration
          </div>
        </div>
      </div>
    `
    
    // Update the header to show verification success
    const headerElement = this.element.querySelector('h3')
    if (headerElement) {
      headerElement.innerHTML = `
        <svg class="w-5 h-5 mr-2 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        Revenue Verified
      `
    }
    
    const descriptionElement = this.element.querySelector('p.text-sm.text-gray-600')
    if (descriptionElement) {
      descriptionElement.textContent = 'Your revenue has been verified through secure bank connection'
    }
    
    // Add the verified content without replacing the entire container
    // This preserves the form structure and all event listeners
    const existingContent = this.element.querySelector('.verification-success-content')
    if (existingContent) {
      existingContent.remove()
    }
    
    // Find a good place to insert the verified content (after the description)
    const insertAfter = descriptionElement || this.element.querySelector('.mb-4')
    if (insertAfter && insertAfter.parentNode) {
      insertAfter.parentNode.insertBefore(verifiedDiv, insertAfter.nextSibling)
    } else {
      // Fallback: append to the element
      this.element.appendChild(verifiedDiv)
    }
    
  }
  
  showError(message) {
    this.hideAllStates()

    if (this.hasErrorIndicatorTarget) {
      this.errorIndicatorTarget.classList.remove('hidden')
      this.errorIndicatorTarget.querySelector('.error-message').textContent = message
    }

    // After Plaid fails, hide the Plaid button and expand manual upload
    this.switchToManualUpload()
  }

  switchToManualUpload() {
    // Find the revenue-verification controller on the same element and expand manual
    const revVerifController = this.application.getControllerForElementAndIdentifier(
      this.element.closest('[data-controller~="revenue-verification"]'),
      "revenue-verification"
    )
    if (revVerifController) {
      revVerifController.expandManual()
      revVerifController.showPlaidFailedBanner("Bank verification was unsuccessful. You can upload your bank statements instead.")
    }
  }
  
  hideLoading() {
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.add('hidden')
    }
    this.enableButtons()
  }

  hideLoadingOnly() {
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.add('hidden')
    }
  }
  
  hideAllStates() {
    if (this.hasLoadingIndicatorTarget) this.loadingIndicatorTarget.classList.add('hidden')
    if (this.hasSuccessIndicatorTarget) this.successIndicatorTarget.classList.add('hidden') 
    if (this.hasErrorIndicatorTarget) this.errorIndicatorTarget.classList.add('hidden')
  }
  
  enableButtons() {
    if (this.hasConnectButtonTarget) this.connectButtonTarget.disabled = false
  }
  
  updateVerificationBadge(badge) {
    const badgeHtml = `
      <div class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${badge.color}-100 text-${badge.color}-800">
        <svg class="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        ${badge.text}
      </div>
    `
    this.verificationBadgeTarget.innerHTML = badgeHtml
  }
  
  showSuccessMessage(message) {
    // Could trigger a toast notification or temporary message
  }
  
  
  formatCurrency(amount) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
  }

  #resetConnecting() {
    this.#isConnecting = false
    this.enableButtons()
  }

  #rateLimitKey() {
    return `plaid_last_attempt_${this.applicationIdValue}`
  }

  #recordAttempt() {
    localStorage.setItem(this.#rateLimitKey(), Date.now())
  }

  #isRateLimited() {
    const last = parseInt(localStorage.getItem(this.#rateLimitKey()) || "0", 10)
    return last > 0 && (Date.now() - last) < RATE_LIMIT_SECONDS * 1000
  }

  #showRateLimitMessage() {
    if (this.hasRateLimitMessageTarget) {
      this.rateLimitMessageTarget.classList.remove('hidden')
      setTimeout(() => {
        this.rateLimitMessageTarget.classList.add('hidden')
      }, 4000)
    }
  }
}